import { isDefined } from "@clipboard-health/util-ts";
import { Text } from "@src/appV2/redesign/system/ui-components";

import { ListLoadingState } from "../../components/ListLoadingState";
import { OpenShiftCardContainer } from "../../Shift/Open/CardContainer";
import { convertShiftToSlotId } from "../../Shift/Open/useGetShiftsSlotV2";
import { useOpenShiftListDataContext } from "../../Shift/Open/useOpenShiftListDataContext";
import { useVirtualShiftListContext } from "../../Shift/Open/useVirtualShiftListContext/useVirtualShiftListContext";
import { ShiftVirtualizedList } from "../../Shift/Open/VirtualizedList";
import { BonusEligibleShiftShoppingEmptyState } from "./EmptyState";

interface BonusEligibleShiftShoppingListProps {
  scrollRef: React.RefObject<HTMLDivElement>;
}

export function BonusEligibleShiftShoppingList(props: BonusEligibleShiftShoppingListProps) {
  const { scrollRef } = props;

  const { listRef, virtualShiftListItems } = useVirtualShiftListContext();

  const isListEmpty = virtualShiftListItems.length === 0;

  const {
    onClickOpenShift,
    workplacesMap,
    shiftsBlockingRequirementsContextMap,
    isOpenShiftsLoading,
    isOpenShiftsError,
    isOpenShiftsSuccess,
    shiftsSlotsMap,
    friendsMap,
    recentColleaguesMap,
    handleRangeChanged,
    shiftAuctionsMap,
    canLoadMore,
    loadMore,
    isLoadingMore,
  } = useOpenShiftListDataContext();

  if (isOpenShiftsError) {
    return <Text align="center">Error loading bonus-eligible shifts</Text>;
  }

  if (isOpenShiftsLoading) {
    return (
      <ListLoadingState
        texts={[
          "Finding shifts with bonus opportunities",
          "Searching for bonus-eligible shifts near you",
          "Your bonus-eligible shifts are loading",
          "Crunching the numbers to maximize your earnings",
          "Unlocking your next great opportunity",
        ]}
      />
    );
  }

  if (isOpenShiftsSuccess && isListEmpty) {
    return <BonusEligibleShiftShoppingEmptyState />;
  }

  return (
    <ShiftVirtualizedList
      ref={listRef}
      scrollRef={scrollRef}
      items={virtualShiftListItems}
      isLoadingMore={isLoadingMore}
      renderItem={(item) => {
        if (item.type !== "open-shift") {
          return null;
        }

        const shift = item;
        const workplace = workplacesMap.get(shift.relationships.workplace.data.id);
        if (!isDefined(workplace)) {
          return null;
        }

        const shiftAuctionId = shift.relationships.shiftAuction?.data.id;
        const shiftAuction = shiftAuctionId ? shiftAuctionsMap.get(shiftAuctionId) : undefined;

        return (
          <OpenShiftCardContainer
            key={shift.id}
            workers={
              shiftsSlotsMap
                .get(
                  convertShiftToSlotId({
                    start: shift.attributes.start,
                    end: shift.attributes.end,
                    facilityId: workplace.id,
                  })
                )
                ?.shiftAssignments.filter((assignment) => assignment.shiftId !== shift.id)
                .map(({ worker }) => worker) ?? []
            }
            shift={shift}
            workplace={workplace}
            blockingRequirementsContext={(() => {
              const context = shiftsBlockingRequirementsContextMap.get(shift.id);
              return context === null ? undefined : context;
            })()}
            friendsMap={friendsMap}
            recentColleaguesMap={recentColleaguesMap}
            shiftAuction={shiftAuction}
            source="bonusEligibleShiftShopping"
            onClick={({ offer }) => {
              onClickOpenShift({
                shift,
                workplace,
                offer,
                shiftAuction,
                source: "bonusEligibleShiftShopping",
              });
            }}
          />
        );
      }}
      onEndReached={() => {
        if (canLoadMore) {
          loadMore();
        }
      }}
      onRangeChanged={handleRangeChanged}
    />
  );
}
